
import NeoCarousel from '@/components/ui/neoCarousel';
import { capitalizeCityName, cn, renderCaptionWithHashtags } from '@/lib/utils';
import { timeAgo } from '@/utils/timeAgo';

import { IonButton, IonButtons, IonContent, IonHeader, IonModal, IonRouterLink, IonTitle, IonToolbar } from '@ionic/react';
import { X } from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';
import OpenedPost from './OpenedPost';
import PostLikes from './PostLikes';
import { PostMenu } from './PostMenu';
import { useTranslation } from 'react-i18next';
import GolfIcon from './GolfIcon';
import { GolfAvatar } from './ui/GolfAvatar';
import LikeButton from './ui/LikeButton';
import GameResultMedia from './game-result/GameResultMedia';

type PostProps = {
  id: number;
  user: {
    id: number;
    userId: string;
    firstName: string;
    lastName: string;
    imageUrl: string;
    username: string;
  };
  media: { type: 'image' | 'video' | 'gif' | 'game_result'; url: string; gameId?: number }[];
  likes: number;
  isLiked: boolean;
  course: {
    id: number;
    name: string;
  };
  caption: string;
  comments: number;
  audience: number;
  profileId: number;
  onAudienceChange?: (postId: number, newAudience: number) => void;
  onDelete: (postId: number) => void;
  className?: string;
  createdAt: string;
  pageRef?: React.RefObject<HTMLElement>;
};

export const Post = React.forwardRef<HTMLDivElement, PostProps>(
  ({ id, user, media, likes, comments, isLiked: initialIsLiked, course, caption, profileId, createdAt, onDelete, pageRef, className }, ref) => {
    const [isLiked, setIsLiked] = useState(initialIsLiked);
    const [likeCount, setLikeCount] = useState(likes);
    const [isExpanded, setIsExpanded] = useState(false);
    const [isTextOverflowing, setIsTextOverflowing] = useState(false);
    const captionRef = useRef<HTMLParagraphElement>(null);
    const [showPost, setShowPost] = useState(false);
    const [showLikes, setShowLikes] = useState(false);

    const { t } = useTranslation('MGP');
    useEffect(() => {
      const checkTextOverflow = () => {
        const element = captionRef.current;
        if (element) {
          // Check if the content height is greater than the line height * 3
          const isOverflowing = element.scrollHeight > (element.clientHeight + 10);
          setIsTextOverflowing(isOverflowing);
        }
      };

      checkTextOverflow();
      // Add resize listener to recheck on window resize
      window.addEventListener('resize', checkTextOverflow);
      return () => window.removeEventListener('resize', checkTextOverflow);
    }, [caption]);

    return (
      <div ref={ref} className={cn("p-1 border-b border-input/50", className)}>
        {/* Post Header */}
        <div className={cn("mb-2 flex items-center justify-between", className)}>
          <div className="flex items-center space-x-2 cursor-pointer">
            <IonRouterLink routerLink={`/u/${user.username}`}>
              <GolfAvatar size={40} imageUrl={user.imageUrl} />
            </IonRouterLink>
            <div className="flex flex-col">
              <div className="flex items-center">
                <IonRouterLink routerLink={`/u/${user.username}`}>
                  <span className="text-base font-semibold">
                    {user.username}
                  </span>
                </IonRouterLink>
              </div>
              <div className="text-sm">
                <span className="text-muted-foreground">
                  {timeAgo(new Date(createdAt), t)}{course && ' · '}
                </span>
                {course && <IonRouterLink routerLink={`/c/${course.id}`} className="text-muted-foreground">{capitalizeCityName(course.name)}</IonRouterLink>}
              </div>
            </div>
          </div>
          <PostMenu
            username={user.username}
            postId={id.toString()}
            onDelete={() => onDelete(id)}
          />
        </div>

        {/* Caption with Show More/Less */}
        {caption && (
          <div className="cursor-pointer mb-2" onClick={() => setShowPost(true)}>
            <p
              ref={captionRef}
              className={cn(
                'text-sm',
                !isExpanded && 'line-clamp-3',
              )}
              style={{
                display: '-webkit-box',
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                ...(isExpanded && { WebkitLineClamp: 'unset' }),
              }}
            >
              {renderCaptionWithHashtags(caption)}
            </p>
            {isTextOverflowing && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setIsExpanded(!isExpanded);
                }}
                className="mt-1 text-sm text-muted-foreground hover:text-foreground"
              >
                {isExpanded ? '' : t('show_more')}
              </button>
            )}
          </div>
        )}

        {/* Game result, images, or just caption */}
        {media && media.length > 0 && (
          <div className="cursor-pointer">
            {media.some(item => item.type === 'game_result') ? (
              // Render game result preview if there's a game_result type media
              <div className="mb-2">
                <GameResultMedia
                  userId={user.userId}
                  gameId={media.find(item => item.type === 'game_result')?.gameId || 0}
                  className="w-full rounded-xl overflow-hidden"
                />
              </div>
            ) : (
              // Otherwise render the carousel for normal media
              <NeoCarousel className='object-contain rounded-md' images={media} />
            )}
          </div>
        )}

        {/* Actions (Like, Comment, Share) */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            {/* Like Button */}
            <div className="flex items-center mx-1 gap-2">
              <LikeButton
                postId={id}
                initialLiked={isLiked}
                initialLikeCount={likeCount}
                userId={profileId?.toString() || ''}
                showCount={false}
                showLabel={true}
                size="md"
                className="text-foreground text-sm"
                onLikeChange={(newIsLiked, newCount) => {
                  setIsLiked(newIsLiked);
                  setLikeCount(newCount);
                }}
              />
              {/* Comments button */}
              <IonButton
                fill='clear'
                shape="round"
                className='text-foreground text-sm'
                style={{ '--padding-start': '0' }}
                onClick={() => setShowPost(true)}
              >
                <GolfIcon name="comment" className="size-6 mr-2" />
                {comments > 0 ? `${comments} ${t('comments').toLowerCase()}` : t('comments')}
              </IonButton>

              {/* OpenedPost Modal */}
              <OpenedPost
                isOpen={showPost}
                onDidDismiss={() => setShowPost(false)}
                postId={id}
                user={user}
                media={media}
                likes={likeCount}
                isLiked={isLiked}
                course={course}
                caption={caption}
                comments={comments}
                profileId={profileId}
                onDelete={onDelete}
                createdAt={createdAt}
              />
            </div>

            {/* Likes Modal */}
            <IonModal
              isOpen={showLikes}
              onDidDismiss={() => setShowLikes(false)}
              className="likes-modal"
              presentingElement={pageRef?.current!}
            >
              <IonHeader>
                <IonToolbar>
                  <IonTitle>{t('liked_by')}</IonTitle>
                  <IonButtons slot="end">
                    <IonButton onClick={() => setShowLikes(false)}>
                      <X className="size-5" />
                    </IonButton>
                  </IonButtons>
                </IonToolbar>
              </IonHeader>
              <IonContent className="ion-padding">
                <PostLikes postId={id} />
              </IonContent>
            </IonModal>
          </div>
        </div>
      </div>
    );
  },
);

Post.displayName = 'Post';
